<template>
  <div class="relative w-full h-full">
    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-2">Подтверждение действия</h2>
          <p class="text-gray-600 mb-6">
            <span v-if="pendingAction === 'approve'">Вы уверены, что хотите <strong>одобрить</strong> это объявление?</span>
            <span v-else-if="pendingAction === 'reject'">Вы уверены, что хотите <strong>отклонить</strong> это объявление?</span>
            <span v-else-if="pendingAction === 'delete'">Вы уверены, что хотите <strong>удалить</strong> это объявление?</span>
          </p>
          <div v-if="pendingAnnouncement" class="bg-gray-50 p-4 rounded mb-6">
            <p class="text-sm"><strong>Название:</strong> {{ pendingAnnouncement.title }}</p>
            <p class="text-sm"><strong>Тип:</strong> {{ pendingAnnouncement.announcement_type === 'request' ? 'Запрос' : 'Предложение' }}</p>
            <p class="text-sm"><strong>Статус:</strong> {{ getStatusDisplay(pendingAnnouncement.status) }}</p>
          </div>
        </div>
        <div class="border-t p-6 flex justify-end gap-3">
          <button
            @click="showConfirmModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition font-semibold"
          >
            Отмена
          </button>
          <button
            @click="executeAction()"
            :class="pendingAction === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : pendingAction === 'delete' ? 'bg-blueGray-500 hover:bg-blueGray-600' : 'bg-red-500 hover:bg-red-600'"
            class="px-4 py-2 text-white rounded transition font-semibold"
          >
            <span v-if="pendingAction === 'approve'">Одобрить</span>
            <span v-else-if="pendingAction === 'reject'">Отклонить</span>
            <span v-else-if="pendingAction === 'delete'">Удалить</span>
          </button>
        </div>
      </div>
    </div>

    <div class="relative pb-32">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full">
        <div class="flex justify-between items-center">
          <div>
            <h1 class=" text-4xl font-bold">Объявления пилотов</h1>
            <p class="text-blueGray-400 mt-2">Управление запросами и предложениями</p>
          </div>
          <router-link
            :to="{ name: 'PilotAnnouncementCreate' }"
            class="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition font-bold text-sm"
          >
            + Создать объявление
          </router-link>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto w-full -mt-24 mb-12">
      <!-- Filters Card -->
      <div class="bg-white rounded-lg shadow-xl p-6 mb-6">
        <h2 class="text-blueGray-800 text-lg font-bold mb-4">Фильтры</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Тип</label>
            <select
              v-model="filters.announcement_type"
              @change="currentPage = 1; fetchAnnouncements()"
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Все типы</option>
              <option value="request">Запрос на услугу</option>
              <option value="offer">Предложение услуги</option>
            </select>
          </div>

          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Статус</label>
            <select
              v-model="filters.status"
              @change="currentPage = 1; fetchAnnouncements()"
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Все статусы</option>
              <option value="pending">На рассмотрении</option>
              <option value="approved">Одобрено</option>
              <option value="rejected">Отклонено</option>
              <option value="archived">Архивировано</option>
            </select>
          </div>

          <div class="md:col-span-2">
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Поиск</label>
            <input
              v-model="filters.search"
              @keyup.enter="currentPage = 1; fetchAnnouncements()"
              placeholder="Поиск по названию или описанию..."
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      <!-- Table Card -->
      <div class="bg-white rounded-lg shadow-xl overflow-hidden">
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="text-center">
            <i class="fas fa-spinner fa-spin text-4xl text-blueGray-400 mb-2"></i>
            <p class="text-blueGray-600">Загрузка объявлений...</p>
          </div>
        </div>

        <div v-else-if="announcements.length === 0" class="text-center py-12">
          <i class="fas fa-inbox text-4xl text-blueGray-300 mb-2"></i>
          <p class="text-blueGray-600">Объявлений не найдено</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-blueGray-50 border-b border-blueGray-200">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Название</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Тип</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Статус</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Автор</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Дата</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="announcement in announcements" :key="announcement.id" class="border-b border-blueGray-200 hover:bg-blueGray-50 transition">
                <td class="px-6 py-4 text-sm text-blueGray-900 font-semibold">{{ announcement.title }}</td>
                <td class="px-6 py-4 text-sm">
                  <span
                    class="px-3 py-1 rounded-full text-xs font-bold"
                    :class="announcement.announcement_type === 'request'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                    "
                  >
                    {{ announcement.announcement_type === 'request' ? 'Запрос' : 'Предложение' }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span
                    class="px-3 py-1 rounded-full text-xs font-bold"
                    :class="getStatusClass(announcement.status)"
                  >
                    {{ getStatusDisplay(announcement.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ announcement.author_name }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ formatDate(announcement.created_at) }}</td>
                <td class="px-6 py-4 text-sm space-x-2">
                  <router-link
                    :to="{ name: 'PilotAnnouncementEdit', params: { id: announcement.id } }"
                    class="inline-block px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-xs font-bold"
                  >
                    Редактировать
                  </router-link>
                  <button
                    v-if="announcement.status === 'pending'"
                    @click="approveAnnouncement(announcement)"
                    class="inline-block px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition text-xs font-bold"
                  >
                    Одобрить
                  </button>
                  <button
                    v-if="announcement.status === 'pending'"
                    @click="rejectAnnouncement(announcement)"
                    class="inline-block px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs font-bold"
                  >
                    Отклонить
                  </button>
                  <button
                    @click="deleteAnnouncement(announcement)"
                    class="inline-block px-3 py-1 bg-blueGray-500 text-white rounded hover:bg-blueGray-600 transition text-xs font-bold"
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex justify-center items-center gap-4">
        <button
          @click="currentPage = Math.max(1, currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          ← Назад
        </button>
        <span class="px-4 py-2 text-gray-700">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="currentPage = Math.min(totalPages, currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { API_PILOT_ANNOUNCEMENTS, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri';

export default {
  name: 'PilotAnnouncementsIndex',
  setup() {
    const announcements = ref([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const perPage = DEFAULT_PER_PAGE;
    const showConfirmModal = ref(false);
    const pendingAction = ref(null);
    const pendingAnnouncement = ref(null);

    const filters = ref({
      announcement_type: '',
      status: '',
      search: ''
    });

    const fetchAnnouncements = async () => {
      loading.value = true;
      try {
        const params = {
          p: currentPage.value,
          page_size: perPage
        };

        if (filters.value.announcement_type) {
          params.announcement_type = filters.value.announcement_type;
        }

        if (filters.value.status) {
          params.status = filters.value.status;
        }

        if (filters.value.search) {
          params.search = filters.value.search;
        }

        const response = await axios.get(`${API_PILOT_ANNOUNCEMENTS.list}`, {
          params,
          headers: HEADERS_TOKEN_JSON()
        });

        if (response.data && response.data.data) {
          announcements.value = response.data.data.data.results || [];
          const count = response.data.data.data.count || 0;
          totalPages.value = Math.ceil(count / perPage) || 1;
          console.log('Announcements loaded:', response.data.data);
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
      } finally {
        loading.value = false;
      }
    };

    const approveAnnouncement = (announcement) => {
      pendingAnnouncement.value = announcement;
      pendingAction.value = 'approve';
      showConfirmModal.value = true;
    };

    const rejectAnnouncement = (announcement) => {
      pendingAnnouncement.value = announcement;
      pendingAction.value = 'reject';
      showConfirmModal.value = true;
    };

    const deleteAnnouncement = (announcement) => {
      pendingAnnouncement.value = announcement;
      pendingAction.value = 'delete';
      showConfirmModal.value = true;
    };

    const executeAction = async () => {
      if (!pendingAnnouncement.value) return;

      try {
        if (pendingAction.value === 'approve') {
          await axios.patch(`${API_PILOT_ANNOUNCEMENTS.base}${pendingAnnouncement.value.id}/`,
            { status: 'approved' },
            { headers: HEADERS_TOKEN_JSON() }
          );
        } else if (pendingAction.value === 'reject') {
          await axios.patch(`${API_PILOT_ANNOUNCEMENTS.base}${pendingAnnouncement.value.id}/`,
            { status: 'rejected' },
            { headers: HEADERS_TOKEN_JSON() }
          );
        } else if (pendingAction.value === 'delete') {
          await axios.delete(`${API_PILOT_ANNOUNCEMENTS.base}${pendingAnnouncement.value.id}/`,
            { headers: HEADERS_TOKEN_JSON() }
          );
        }
        showConfirmModal.value = false;
        pendingAnnouncement.value = null;
        pendingAction.value = null;
        await fetchAnnouncements();
      } catch (error) {
        console.error('Error executing action:', error);
      }
    };

    const getStatusDisplay = (status) => {
      const statuses = {
        pending: 'На рассмотрении',
        approved: 'Одобрено',
        rejected: 'Отклонено',
        archived: 'Архивировано'
      };
      return statuses[status] || status;
    };

    const getStatusClass = (status) => {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        approved: 'bg-emerald-100 text-emerald-800',
        rejected: 'bg-red-100 text-red-800',
        archived: 'bg-blueGray-100 text-blueGray-800'
      };
      return classes[status] || 'bg-blueGray-100 text-blueGray-800';
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    };

    watch(currentPage, () => {
      fetchAnnouncements();
    });

    onMounted(() => {
      fetchAnnouncements();
    });

    return {
      announcements,
      loading,
      currentPage,
      totalPages,
      filters,
      showConfirmModal,
      pendingAction,
      pendingAnnouncement,
      fetchAnnouncements,
      approveAnnouncement,
      rejectAnnouncement,
      deleteAnnouncement,
      executeAction,
      getStatusDisplay,
      getStatusClass,
      formatDate
    };
  }
};
</script>

<style scoped>
</style>
